// discount-codes/enums.ts
var DiscountType = /* @__PURE__ */ ((DiscountType2) => {
  DiscountType2["PERCENTAGE"] = "percentage";
  DiscountType2["FIXED_AMOUNT"] = "fixed_amount";
  return DiscountType2;
})(DiscountType || {});
var DiscountAppliesTo = /* @__PURE__ */ ((DiscountAppliesTo2) => {
  DiscountAppliesTo2["ORDER_TOTAL"] = "order_total";
  DiscountAppliesTo2["LINE_ITEMS"] = "line_items";
  DiscountAppliesTo2["SHIPPING"] = "shipping";
  return DiscountAppliesTo2;
})(DiscountAppliesTo || {});

// discount-codes/discount-codes.schema.ts
import { z } from "zod";
var DiscountCodeSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(50),
  discountType: z.nativeEnum(DiscountType),
  discountValue: z.string(),
  appliesTo: z.nativeEnum(DiscountAppliesTo),
  brandId: z.number().int().positive().nullable(),
  minOrderAmount: z.string(),
  maxDiscountAmount: z.string().nullable(),
  allowedCustomerIds: z.array(z.string().uuid()).nullable(),
  excludeSaleItems: z.boolean(),
  validFrom: z.coerce.date(),
  validUntil: z.coerce.date().nullable(),
  isActive: z.boolean(),
  maxTotalUses: z.number().int().positive().nullable(),
  maxUsesPerCustomer: z.number().int().positive(),
  currentTotalUses: z.number().int().min(0),
  stacksWithOtherDiscounts: z.boolean(),
  priority: z.number().int(),
  hostDomain: z.string().min(1).max(255),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
  description: z.string().nullable()
});
var DiscountCodeValidationResultSchema = z.object({
  isValid: z.boolean(),
  reason: z.string().optional(),
  discountCode: DiscountCodeSchema.optional(),
  calculatedDiscount: z.object({
    amount: z.string(),
    percentage: z.number().optional()
  }).optional()
});
var CreateDiscountCodeDtoSchema = z.object({
  code: z.string().min(1).max(50),
  discountType: z.nativeEnum(DiscountType),
  discountValue: z.string(),
  appliesTo: z.nativeEnum(DiscountAppliesTo).optional(),
  brandId: z.number().int().positive().optional(),
  minOrderAmount: z.string().optional(),
  maxDiscountAmount: z.string().optional(),
  allowedCustomerIds: z.array(z.string().uuid()).optional(),
  excludeSaleItems: z.boolean().optional(),
  validFrom: z.coerce.date().optional(),
  validUntil: z.coerce.date().optional(),
  isActive: z.boolean().optional(),
  maxTotalUses: z.number().int().positive().optional(),
  maxUsesPerCustomer: z.number().int().positive().optional(),
  stacksWithOtherDiscounts: z.boolean().optional(),
  priority: z.number().int().optional(),
  hostDomain: z.string().min(1).max(255),
  description: z.string().optional(),
  createdBy: z.string().uuid().optional()
});
var UpdateDiscountCodeDtoSchema = z.object({
  code: z.string().min(1).max(50).optional(),
  discountType: z.nativeEnum(DiscountType).optional(),
  discountValue: z.string().optional(),
  appliesTo: z.nativeEnum(DiscountAppliesTo).optional(),
  brandId: z.number().int().positive().optional(),
  minOrderAmount: z.string().optional(),
  maxDiscountAmount: z.string().optional(),
  allowedCustomerIds: z.array(z.string().uuid()).optional(),
  excludeSaleItems: z.boolean().optional(),
  validFrom: z.coerce.date().optional(),
  validUntil: z.coerce.date().optional(),
  isActive: z.boolean().optional(),
  maxTotalUses: z.number().int().positive().optional(),
  maxUsesPerCustomer: z.number().int().positive().optional(),
  stacksWithOtherDiscounts: z.boolean().optional(),
  priority: z.number().int().optional(),
  description: z.string().optional()
});
var FindDiscountCodesDtoSchema = z.object({
  code: z.string().optional(),
  hostDomain: z.string().optional(),
  isActive: z.boolean().optional(),
  brandId: z.number().int().positive().optional(),
  includeDeleted: z.boolean().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().min(0).optional()
});
var ValidateDiscountCodeDtoSchema = z.object({
  code: z.string().min(1).max(50),
  hostDomain: z.string().min(1).max(255),
  customerId: z.string().optional(),
  orderTotal: z.string(),
  brandId: z.number().int().positive().optional(),
  hasSaleItems: z.boolean().optional()
});
var ApplyDiscountCodeDtoSchema = z.object({
  code: z.string().min(1).max(50),
  hostDomain: z.string().min(1).max(255),
  customerId: z.string(),
  orderId: z.number().int().positive().optional(),
  orderTotal: z.string(),
  discountAmount: z.string()
});
var DiscountCodeResponseDtoSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  discountType: z.nativeEnum(DiscountType),
  discountValue: z.string(),
  appliesTo: z.nativeEnum(DiscountAppliesTo),
  brandId: z.number().int().positive().nullable(),
  minOrderAmount: z.string(),
  maxDiscountAmount: z.string().nullable(),
  validFrom: z.string(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
  maxTotalUses: z.number().int().positive().nullable(),
  maxUsesPerCustomer: z.number().int().positive(),
  currentTotalUses: z.number().int().min(0),
  stacksWithOtherDiscounts: z.boolean(),
  priority: z.number().int(),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
var ValidateDiscountCodeResponseDtoSchema = z.object({
  isValid: z.boolean(),
  reason: z.string().optional(),
  discountCode: DiscountCodeResponseDtoSchema.optional(),
  calculatedDiscount: z.object({
    amount: z.string(),
    percentage: z.number().optional()
  }).optional()
});
var DeleteDiscountCodeDtoSchema = z.object({
  id: z.string().uuid()
});
var BulkDiscountCodeResultDtoSchema = z.object({
  totalProcessed: z.number().int().min(0),
  successful: z.number().int().min(0),
  failed: z.number().int().min(0),
  errors: z.array(
    z.object({
      code: z.string(),
      error: z.string()
    })
  )
});
var DiscountCodeQueryFiltersSchema = z.object({
  code: z.string().optional(),
  hostDomain: z.string().optional(),
  isActive: z.boolean().optional(),
  brandId: z.number().int().positive().optional(),
  validAtDate: z.coerce.date().optional(),
  includeDeleted: z.boolean().optional(),
  customerId: z.string().optional()
});
var DiscountCodeStatsSchema = z.object({
  totalActive: z.number().int().min(0),
  totalExpired: z.number().int().min(0),
  totalUsed: z.number().int().min(0),
  totalAvailable: z.number().int().min(0),
  mostUsedCodes: z.array(
    z.object({
      code: z.string(),
      uses: z.number().int().min(0)
    })
  ),
  recentlyExpired: z.number().int().min(0)
});

export {
  DiscountType,
  DiscountAppliesTo,
  DiscountCodeSchema,
  DiscountCodeValidationResultSchema,
  CreateDiscountCodeDtoSchema,
  UpdateDiscountCodeDtoSchema,
  FindDiscountCodesDtoSchema,
  ValidateDiscountCodeDtoSchema,
  ApplyDiscountCodeDtoSchema,
  DiscountCodeResponseDtoSchema,
  ValidateDiscountCodeResponseDtoSchema,
  DeleteDiscountCodeDtoSchema,
  BulkDiscountCodeResultDtoSchema,
  DiscountCodeQueryFiltersSchema,
  DiscountCodeStatsSchema
};
